import {
  Drawer,
  Row,
  Col,
  Input,
  Button,
  Form,
  Popconfirm,
  Select,
  Typography,
  Radio,
} from "antd";
import { SERVICE_REQUEST_TYPES } from "~/utils";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { useNewServiceRequestButton } from "~/hooks";
import * as APITypes from "~/API";

const getEntityTypeOptions = (
  t: TFunction<"translation">
): { value: APITypes.EntityType; text: string }[] => [
  {
    value: APITypes.EntityType.Property,
    text: t("Property"),
  },
  {
    value: APITypes.EntityType.Building,
    text: t("Building"),
  },
  {
    value: APITypes.EntityType.Unit,
    text: t("Unit"),
  },
  {
    value: APITypes.EntityType.Parcel,
    text: t("Parcel"),
  },
];

export const NewServiceRequestButton = ({
  initialValues,
  tenantList,
  ownerID,
  ownerType,
  canCreateRequest,
}: {
  initialValues: {
    creatorID?: string;
    creatorName?: string;
    handlerID?: string;
    handlerName?: string;
    watcherID?: string;
    watcherName?: string;
    entityID?: string;
    entityType?: APITypes.EntityType;
    tenantID?: string;
  };
  tenantList: APITypes.Tenant[];
  ownerID: string;
  ownerType: APITypes.OwnerType;
  canCreateRequest: boolean;
}) => {
  const { t } = useTranslation();
  const useNewRequest = useNewServiceRequestButton(
    ownerID,
    ownerType,
    initialValues,
    canCreateRequest
  );

  return (
    <>
      <Button
        onClick={useNewRequest.toggleCreateDrawer}
        type="primary"
        ghost
        disabled={
          useNewRequest.isLoading ||
          useNewRequest.isSubmitting ||
          !canCreateRequest
        }
        loading={useNewRequest.isSubmitting}
        title={
          !canCreateRequest
            ? t("No_Permission_To_Create_Service_Requests")
            : undefined
        }
      >
        {t("Create_Request")}
      </Button>

      <Drawer
        open={useNewRequest.showCreateDrawer}
        onClose={useNewRequest.toggleCreateDrawer}
        title={t("Create_Request")}
        width={700}
      >
        <Form
          form={useNewRequest.formInstance}
          onFinish={useNewRequest.handleCreateRequestSubmit}
          initialValues={initialValues}
        >
          <Row gutter={24} style={{ marginBottom: "2rem" }}>
            <Col span={24}>
              <Form.Item
                label={t("Title")}
                name="title"
                rules={[
                  {
                    required: true,
                    message: t("Enter_a_title"),
                  },
                ]}
              >
                <Input placeholder={t("Enter_a_title")} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24} style={{ marginBottom: "2rem" }}>
            <Col span={24}>
              <Form.Item
                label={t("Description")}
                name="description"
                rules={[
                  {
                    required: true,
                    message: t("Enter_a_description"),
                  },
                ]}
              >
                <Input.TextArea placeholder={t("Enter_a_description")} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24} style={{ marginBottom: "2rem" }}>
            <Col span={12}>
              <Form.Item
                label={t("Estimated_Cost")}
                name="estimatedCost"
                rules={[
                  {
                    required: true,
                    message: t("Enter_a_cost_estimate"),
                  },
                ]}
              >
                <Input placeholder={t("Enter_a_cost_estimate")} type="number" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={t("Type")}
                name="type"
                rules={[
                  {
                    required: true,
                    message: t("Select_a_request_type"),
                  },
                ]}
              >
                <Select placeholder={t("Select_a_request_type")}>
                  {SERVICE_REQUEST_TYPES.map((type) => {
                    return (
                      <Select.Option key={type} value={type}>
                        {t(type as any)}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24} style={{ marginBottom: "2rem" }}>
            <Col span={24}>
              <Form.Item
                label={t("Entity_Type")}
                name="entityType"
                rules={[
                  {
                    required: true,
                    message: t("Select_an_entity_type"),
                  },
                ]}
              >
                <Select
                  onSelect={useNewRequest.handle_EntityTypeOnChange}
                  placeholder={t("Select_an_entity")}
                >
                  {getEntityTypeOptions(t).map((type) => {
                    return (
                      <Select.Option key={type.value} value={type.value}>
                        {type.text}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24} style={{ marginBottom: "2rem" }}>
            <Col span={24}>
              <Form.Item
                label={
                  <Typography.Text>
                    {t("Entity_ID")}{" "}
                    <Typography.Text type="secondary">{`(${useNewRequest.entityListOptions.length})`}</Typography.Text>
                  </Typography.Text>
                }
                name="entityID"
                rules={[
                  {
                    required: true,
                    message: t("Select_an_entity"),
                  },
                ]}
              >
                <Select
                  loading={useNewRequest.is_EntityTypeLoading}
                  placeholder={t("Select_an_entity")}
                  disabled={
                    !useNewRequest.formInstance.getFieldValue("entityType")
                  }
                >
                  {useNewRequest.entityListOptions.map((option) => {
                    return (
                      <Select.Option value={option.value} key={option.value}>
                        {option.text}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24} style={{ marginBottom: "2rem" }}>
            <Col span={12}>
              <Form.Item
                label={t("Emergency")}
                name="emergency"
                rules={[
                  {
                    required: true,
                    message: t("Select_emergency"),
                  },
                ]}
              >
                <Radio.Group
                  options={[
                    { value: true, label: "Yes" },
                    { value: false, label: "No" },
                  ]}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={t("Upgrade")}
                name="isUpgrade"
                rules={[
                  {
                    required: true,
                    message: t("Select_upgrade_or_repair"),
                  },
                ]}
              >
                <Radio.Group
                  options={[
                    { value: true, label: "Yes" },
                    { value: false, label: "No" },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24} style={{ marginBottom: "2rem" }}>
            <Col span={12}>
              <Form.Item
                label={t("Handled_By")}
                name="handlerName"
                rules={[
                  {
                    required: true,
                    message: t("enter_username"),
                  },
                ]}
              >
                <Input placeholder={t("enter_username")} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <Typography.Text>
                    {t("Tenant")}{" "}
                    <Typography.Text type="secondary">{`(${tenantList.length})`}</Typography.Text>
                  </Typography.Text>
                }
                name="tenantName"
              >
                <Select placeholder={t("Enter_a_name")}>
                  {tenantList.map((option) => {
                    return (
                      <Select.Option
                        value={`${option.firstName} ${option.lastName}`}
                        key={option.id}
                      >
                        {option.firstName} {option.lastName}
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24} style={{ marginBottom: "2rem" }}>
            <Col span={12}>
              <Form.Item
                label={t("Contractor")}
                name="contractor"
                rules={[
                  {
                    required: true,
                    message: t("Enter_a_contractor"),
                  },
                ]}
              >
                <Input placeholder={t("Enter_a_contractor")} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t("Observed_By")} name="watcherName">
                <Input placeholder={t("enter_username")} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24} style={{ marginBottom: "2rem" }}>
            <Col span={12}>
              <Form.Item label={t("Created_By")} name="creatorName">
                <Input disabled placeholder={t("enter_username")} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t("Observed_By")} name="watcherName">
                <Input placeholder={t("enter_username")} />
              </Form.Item>
            </Col>
          </Row>

          <div
            style={{
              borderTop: "1px solid lightgrey",
              display: "flex",
              justifyContent: "space-between",
              paddingTop: "1rem",
            }}
          >
            <Button onClick={() => useNewRequest.formInstance.resetFields()}>
              {t("Reset_Form")}
            </Button>
            <Popconfirm
              title={t("Confirm_changes")}
              onConfirm={() => {
                useNewRequest.formInstance.submit();
                useNewRequest.toggleSubmitConfirm();
              }}
              open={useNewRequest.showSubmitConfirm}
              onCancel={useNewRequest.toggleSubmitConfirm}
            >
              <Button
                type="primary"
                onClick={useNewRequest.toggleSubmitConfirm}
                disabled={useNewRequest.isLoading}
                loading={useNewRequest.isLoading}
              >
                {t("Submit")}
              </Button>
            </Popconfirm>
          </div>
        </Form>
      </Drawer>
    </>
  );
};
